import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ImageCapture } from '@/components/plants/ImageCapture';
import { PlantResult } from '@/components/plants/PlantResult';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { addDays, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Pencil, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { MobileHeader } from '@/components/layout/MobileHeader';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { useSubscription } from '@/hooks/useSubscription';
import { UpgradeDialog } from '@/components/subscription/UpgradeDialog';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';

export interface PlantInfo {
  popular_name: string;
  scientific_name: string;
  planting_season: string;
  soil_type: string;
  sunlight_needs: string;
  water_needs: string;
  cultivation_tips: string;
  watering_frequency_days?: number;
}

// Helper function to determine watering frequency from plant info
const getWateringFrequency = (plantInfo: PlantInfo): number => {
  if (plantInfo.watering_frequency_days && plantInfo.watering_frequency_days > 0) {
    return plantInfo.watering_frequency_days;
  }
  
  const waterNeeds = (plantInfo.water_needs || '').toLowerCase();
  
  if (waterNeeds.includes('abundante') || waterNeeds.includes('frequente') || waterNeeds.includes('muita')) {
    return 3;
  }
  if (waterNeeds.includes('pouca') || waterNeeds.includes('esparsa') || waterNeeds.includes('rara')) {
    return 14;
  }
  return 7;
};

export default function Identify() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [identifying, setIdentifying] = useState(false);
  const [plantInfo, setPlantInfo] = useState<PlantInfo | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [lastWateringDate, setLastWateringDate] = useState<Date>(new Date());
  const [frequencyDays, setFrequencyDays] = useState<number>(7);
  const [isEditingFrequency, setIsEditingFrequency] = useState(false);
  const [editFrequency, setEditFrequency] = useState<string>('7');
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeDialogType, setUpgradeDialogType] = useState<'ai' | 'plants'>('ai');

  const { 
    plan, 
    monthlyUsage, 
    plantCount, 
    canIdentify, 
    canAddPlant, 
    loading: subscriptionLoading,
    logUsage,
    refreshSubscription,
    limits 
  } = useSubscription();

  const handleImageCapture = async (base64: string, file: File) => {
    // Check if user can identify
    if (!canIdentify) {
      setUpgradeDialogType('ai');
      setShowUpgradeDialog(true);
      return;
    }

    setImageBase64(base64);
    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setPlantInfo(null);
    setSaved(false);

    // Analytics: first identification attempt for this user
    try {
      const key = `verdeia_first_id_${user?.id ?? 'anon'}`;
      if (typeof window !== 'undefined' && !localStorage.getItem(key)) {
        const { trackEvent } = await import('@/lib/gtag');
        const { getAnalyticsContext } = await import('@/lib/utm');
        trackEvent('first_identification_started', getAnalyticsContext());
        localStorage.setItem(key, '1');
      }
    } catch { /* noop */ }

    await identifyPlant(base64);
  };


  const identifyPlant = async (base64: string) => {
    setIdentifying(true);

    try {
      const { data, error } = await supabase.functions.invoke('identify-plant', {
        body: { imageBase64: base64 },
      });

      if (error) {
        console.error('Error identifying plant:', error);
        toast.error('Erro ao identificar planta. Tente novamente.');
        return;
      }

      if (data.error) {
        toast.error(data.error);
        return;
      }

      // Log usage after successful identification
      await logUsage('identification');

      setPlantInfo(data);
      const freq = getWateringFrequency(data);
      setFrequencyDays(freq);
      setEditFrequency(freq.toString());
      toast.success('Planta identificada!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao identificar planta. Tente novamente.');
    } finally {
      setIdentifying(false);
    }
  };

  const handleSave = async () => {
    if (!plantInfo || !imageFile || !user) {
      toast.error('Você precisa estar logado para salvar');
      return;
    }

    // Check if user can add more plants
    if (!canAddPlant) {
      setUpgradeDialogType('plants');
      setShowUpgradeDialog(true);
      return;
    }

    setSaving(true);

    try {
      const fileName = `${user.id}/${Date.now()}-${imageFile.name}`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('plant-images')
        .upload(fileName, imageFile);

      if (uploadError) {
        console.error('Error uploading image:', uploadError);
        toast.error('Erro ao salvar imagem');
        return;
      }

      const { data: urlData } = supabase.storage
        .from('plant-images')
        .getPublicUrl(fileName);

      const { data: insertedPlant, error: dbError } = await supabase
        .from('plant_identifications')
        .insert({
          user_id: user.id,
          image_url: urlData.publicUrl,
          popular_name: plantInfo.popular_name,
          scientific_name: plantInfo.scientific_name,
          planting_season: plantInfo.planting_season,
          soil_type: plantInfo.soil_type,
          sunlight_needs: plantInfo.sunlight_needs,
          water_needs: plantInfo.water_needs,
          cultivation_tips: plantInfo.cultivation_tips,
        })
        .select('id')
        .single();

      if (dbError) {
        console.error('Error saving to database:', dbError);
        toast.error('Erro ao salvar na coleção');
        return;
      }

      const nextCareDate = addDays(lastWateringDate, frequencyDays);
      
      const { error: scheduleError } = await supabase
        .from('plant_care_schedules')
        .insert({
          plant_id: insertedPlant.id,
          user_id: user.id,
          care_type: 'watering',
          frequency_days: frequencyDays,
          next_care_date: format(nextCareDate, 'yyyy-MM-dd'),
        });

      if (scheduleError) {
        console.error('Error creating care schedule:', scheduleError);
      }

      // Refresh subscription to update plant count
      await refreshSubscription();

      setSaved(true);
      toast.success('Planta salva na sua coleção!');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao salvar. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleNewIdentification = () => {
    setImageBase64(null);
    setImageFile(null);
    setPreviewUrl(null);
    setPlantInfo(null);
    setSaved(false);
    setLastWateringDate(new Date());
    setFrequencyDays(7);
    setEditFrequency('7');
    setIsEditingFrequency(false);
  };

  const handleSaveFrequency = () => {
    const parsed = parseInt(editFrequency, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 365) {
      setFrequencyDays(parsed);
      setIsEditingFrequency(false);
    } else {
      toast.error('Frequência deve ser entre 1 e 365 dias');
    }
  };

  const handleCancelFrequencyEdit = () => {
    setEditFrequency(frequencyDays.toString());
    setIsEditingFrequency(false);
  };

  return (
    <div className="min-h-screen bg-background pb-28">
      <MobileHeader title="Identificar" showLogo />

      <main className="px-4 py-4 max-w-lg mx-auto">
        {/* Usage indicator for free users */}
        {(subscriptionLoading || plan === 'free') && !plantInfo && (
          <div className="mb-4">
            <UsageIndicator
              plan={plan}
              monthlyUsage={monthlyUsage}
              plantCount={plantCount}
              maxAiUsage={limits.monthlyAiUsage}
              maxPlants={limits.maxPlants}
              onUpgradeClick={() => {
                setUpgradeDialogType('ai');
                setShowUpgradeDialog(true);
              }}
              isLoading={subscriptionLoading}
            />
          </div>
        )}

        {plantInfo && previewUrl ? (
          <>
            <PlantResult
              plant={plantInfo}
              imageUrl={previewUrl}
              onSave={handleSave}
              saving={saving}
              saved={saved}
            />
            
            {/* Watering settings */}
            {!saved && (
              <div className="mt-4 p-4 bg-card rounded-lg border border-border space-y-4">
                {/* Frequency editor */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    💧 Frequência de rega
                  </label>
                  {isEditingFrequency ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={editFrequency}
                        onChange={(e) => setEditFrequency(e.target.value)}
                        className="flex-1 h-11"
                        autoFocus
                      />
                      <span className="text-sm text-muted-foreground">dias</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleSaveFrequency}
                        className="h-9 w-9"
                      >
                        <Check className="h-4 w-4 text-nature" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleCancelFrequencyEdit}
                        className="h-9 w-9"
                      >
                        <X className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">
                        A cada <strong className="text-foreground">{frequencyDays}</strong> {frequencyDays === 1 ? 'dia' : 'dias'}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsEditingFrequency(true)}
                        className="h-8 w-8"
                      >
                        <Pencil className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Date picker for last watering */}
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">
                    📅 Quando foi a última rega?
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal h-11",
                          !lastWateringDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {lastWateringDate ? (
                          format(lastWateringDate, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
                        ) : (
                          <span>Selecione a data</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={lastWateringDate}
                        onSelect={(date) => date && setLastWateringDate(date)}
                        disabled={(date) => date > new Date()}
                        initialFocus
                        className="pointer-events-auto"
                        locale={ptBR}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <p className="text-xs text-muted-foreground">
                  Próxima rega: <strong>{format(addDays(lastWateringDate, frequencyDays), "dd 'de' MMMM", { locale: ptBR })}</strong>
                </p>
              </div>
            )}
            
            <Button
              variant="outline"
              onClick={handleNewIdentification}
              className="w-full mt-4 h-11 press-scale touch-target"
            >
              Identificar outra planta
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="text-center">
              <h1 className="text-lg font-bold text-foreground font-display mb-1">
                Identificar Planta
              </h1>
              <p className="text-sm text-muted-foreground">
                Tire uma foto ou faça upload de uma imagem
              </p>
            </div>

            <ImageCapture
              onImageCapture={handleImageCapture}
              loading={identifying}
            />
          </div>
        )}
      </main>

      <BottomNavigation />

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        currentUsage={upgradeDialogType === 'ai' ? monthlyUsage : plantCount}
        maxUsage={upgradeDialogType === 'ai' ? limits.monthlyAiUsage : limits.maxPlants}
        limitType={upgradeDialogType}
      />
    </div>
  );
}
